import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { IPlaceable } from '../timeline.component';
@Component({
  selector: 'dcc-device-timeline-row',
  templateUrl: './device-timeline-row.component.html',
  styleUrls: ['./device-timeline-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceTimelineRowComponent {
  @Input() title = '';
  @Input() data: IPlaceable[] = [];
  @Input() dotColor = 'lightslategray';
  @ViewChild('definitionsModal') definitionsModal: any;

  readonly rowColor = '#C8D8FA';
  readonly noDataRowColor = '#4C84FF26';

  readonly stateColors: Record<string, string> = {
    Active: '#1B8E6D',
    Awake: '#71B3B4',
    'Awake (Countdown)': '#00C6D2',
    Communicating: '#4C84FF',
    Hibernating: '#7D7FFF',
    'Deep Sleep': '#4A2DA6',
    Offline: '#F94E4E',
  };


  showTooltip = false;
  isOverContainer = false;
  operatingStateData: any[] = [];

  showHintTooltip = false;
  hoverBarTooltipX: unknown;
  hint: string;

  columnDefs: ColDef[] = [
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'ignition', headerName: 'Ignition', width: 120 },
    {
      field: 'transmittingReceivingData',
      headerName: 'Transmitting and Receiving Data',
      width: 200,
    },
    {
      field: 'recordingDvrVideoEvents',
      headerName: 'Recording DVR Video and Events',
      width: 200,
    },
    { field: 'livestreamDvr', headerName: 'Livestream DVR', width: 150 },
    { field: 'trigger', headerName: 'Trigger', width: 180 },
    { field: 'notes', headerName: 'Notes', width: 250 },
  ];
  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };
  private readonly tooltipWidth = 270; // Adjust this value based on your tooltip's width
  private readonly paddingRight = 2; // Padding to avoid the tooltip touching the edge
  private readonly paddingLeft = 40;

  private gridApi!: GridApi;
  private modalRef: NgbModalRef;
  constructor(
    private modalService: NgbModal,
    private http: HttpClient,
  ) {}

  onHoverOverEventTimelineDot(
    dataElement: IPlaceable,
    event: MouseEvent,
  ): void {
    /*
    Event timeline dot tooltip positioning logic: If the tooltip is going to overflow the viewport, position it on the left side of the dot.
    Otherwise, position it on the right side of the dot. This is done by using right.ps or left.px binding in the template.
    Padding is added to avoid the tooltip touching the edge of the viewport.
    */
    /*Event timeline dot tooltip and operational state tooltip is updated to be shown on the middle of the timeline segment 
   rather than dynamically move around the timeline data for better user experience*/
    const segmentMiddle = dataElement.left + dataElement.width / 2;
    const timelineContainer = (event.currentTarget as HTMLElement).closest(
      '.timeline-segments',
    ) as HTMLElement;
    const containerWidth = timelineContainer?.offsetWidth || 0;
    const pixelPosition = (segmentMiddle * containerWidth) / 100;
    this.hoverBarTooltipX = {
      'left.px': pixelPosition,
      transform: 'translateX(-50%)',
    };
    this.hint = dataElement.hint;

    if (this.hint) {
      this.showHintTooltip = true;
    }
  }

  onLeaveFromEventTimelineDot(): void {
    this.showHintTooltip = false;
  }

  onContainerMouseEnter(): void {
    if (this.title === 'Operating State') {
      this.isOverContainer = true;
      this.updateTooltipVisibility();
    }
  }

  onContainerMouseLeave(): void {
    this.isOverContainer = false;
    this.updateTooltipVisibility();
  }

  showHeaderTooltip(): void {
    if (this.title === 'Operating State') {
      this.isOverContainer = true;
      this.updateTooltipVisibility();
    }
  }

  viewDefinitions(): void {
    this.http.get<any[]>('/assets/operating-state.json').subscribe(
      (data) => {
        this.operatingStateData = data.map((s) => ({
          ...s,
          color: this.stateColors[s.state] ?? '#667892',
        }));
        this.modalRef = this.modalService.open(this.definitionsModal, {
          backdrop: false,
          centered: false,
          windowClass: 'modal-no-backdrop',
        });

        setTimeout(() => {
          this.makeModalDraggable();
        }, 100);
      },
      (error) => {
        console.error('Error loading operating state data:', error);
      },
    );
  }

  makeModalDraggable(): void {
    const modalElement = document.querySelector(
      '.draggable-modal',
    ) as HTMLElement;
    const modalHeader = document.getElementById(
      'modalDragHandle',
    ) as HTMLElement;

    if (!modalElement || !modalHeader) return;

    let isDragging = false;
    let offsetX = 0,
      offsetY = 0;

    modalHeader.addEventListener('mousedown', (e: MouseEvent) => {
      isDragging = true;
      const modalRect = modalElement.getBoundingClientRect();
      offsetX = e.clientX - modalRect.left;
      offsetY = e.clientY - modalRect.top;
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (!isDragging) return;

      const newLeft = Math.max(
        0,
        Math.min(
          e.clientX - offsetX,
          window.innerWidth - modalElement.offsetWidth,
        ),
      );
      const newTop = Math.max(
        0,
        Math.min(
          e.clientY - offsetY,
          window.innerHeight - modalElement.offsetHeight,
        ),
      );

      modalElement.style.position = 'fixed';
      modalElement.style.margin = '0';
      modalElement.style.left = `${newLeft}px`;
      modalElement.style.top = `${newTop}px`;
    });

    document.addEventListener('mouseup', () => {
      isDragging = false;
    });
  }
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setRowData(this.operatingStateData);
    this.gridApi.sizeColumnsToFit();
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }
  private updateTooltipVisibility(): void {
    this.showTooltip = this.isOverContainer;
  }
}
